import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useAPI } from '../../../../Context/MyContext';

const KB_MODELS_BY_PROVIDER = {
  whatgrowpro: 'whatgrowpro',
  google: 'gemini-1.5-pro',
  openai: 'gpt-4o-mini',
  openrouter: 'deepseek/deepseek-chat'
};

export default function KnowledgeBaseSettings({ cfg, data, update }) {
  const { uploadPdf, fetchFiles, deleteKnowledgeFile } = useAPI();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadMessage, setUploadMessage] = useState('');
  const [userFiles, setUserFiles] = useState([]);

  const selectedPhoneId = useMemo(() => {
    const explicit = cfg.phone_number_id || cfg.phoneNumberId || '';
    if (explicit && !String(explicit).includes('{{')) return explicit;
    return data?.workflowPhoneId || '';
  }, [cfg.phone_number_id, cfg.phoneNumberId, data?.workflowPhoneId]);

  useEffect(() => {
    if (!selectedPhoneId) {
      setUserFiles([]);
      return;
    }

    fetchFiles(selectedPhoneId)
      .then((res) => {
        const files = Array.isArray(res?.files) ? res.files : [];
        setUserFiles(files);
        update({ userFiles: files });
      })
      .catch((err) => {
        toast.error(err?.message || 'Failed to fetch files');
        setUserFiles([]);
      });
  }, [selectedPhoneId]);

  useEffect(() => {
    setUploadStatus(null);
    setUploadMessage('');
    setSelectedFile(null);
  }, [selectedPhoneId]);

  const provider = cfg.provider || 'whatgrowpro';

  const isUploadValid = () => {
    if (!selectedFile) return false;
    if (!selectedPhoneId) return false;
    if (provider !== 'whatgrowpro' && !cfg.apiKey?.trim()) return false;
    return true;
  };

  const updateProvider = (nextProvider) => {
    update({
      provider: nextProvider,
      model: KB_MODELS_BY_PROVIDER[nextProvider] || cfg.model || 'whatgrowpro'
    });
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    if (!selectedPhoneId) {
      toast.error('Please select a WhatsApp Business first');
      return;
    }

    if (provider !== 'whatgrowpro' && !cfg.apiKey?.trim()) {
      toast.error(`API key is required for ${provider}`);
      return;
    }

    try {
      setUploadStatus('loading');
      setUploadMessage('');

      const payload = {
        provider,
        model: cfg.model,
        apiKey: cfg.apiKey,
        phone_number_id: selectedPhoneId
      };

      const res = await uploadPdf(selectedFile, payload);
      setUploadStatus('success');
      setUploadMessage(res?.message || 'Uploaded successfully!');
      setSelectedFile(null);

      const filesRes = await fetchFiles(selectedPhoneId);
      const files = Array.isArray(filesRes?.files) ? filesRes.files : [];
      setUserFiles(files);
      update({ userFiles: files });
    } catch (err) {
      setUploadStatus('error');
      setUploadMessage(err?.response?.data?.error || 'Upload failed');
    }
  };

  const handleDeleteFile = async (file) => {
    if (!window.confirm(`Delete "${file.file_name}"?`)) return;

    try {
      await deleteKnowledgeFile({
        phone_number_id: selectedPhoneId,
        file_id: file.file_id
      });

      setUserFiles((prev) => prev.filter((f) => f.file_id !== file.file_id));
      toast.success('File deleted successfully');
    } catch (err) {
      toast.error(err?.message || 'Failed to delete file');
    }
  };

  return (
    <div className="mt-2 grid gap-2">
      <div className="grid gap-2 grid-cols-1 sm:grid-cols-2">
        <label className="grid gap-1 text-xs text-slate-700">
          AI Provider
          <select
            className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20"
            value={provider}
            onChange={(e) => updateProvider(e.target.value)}
          >
            <option value="whatgrowpro">WhatGrowPro</option>
            {/* <option value="google">Google Gemini</option>
            <option value="openai">OpenAI</option> */}
            <option value="openrouter">OpenRouter</option>
          </select>
        </label>

        <label className="grid gap-1 text-xs text-slate-700">
          Model
          <input
            disabled
            value={cfg.model || ''}
            className="nodrag w-full rounded-lg border border-slate-200 bg-slate-100 px-2 py-1.5 text-xs text-slate-500"
          />
        </label>
      </div>

      {provider !== 'whatgrowpro' ? (
        <label className="grid gap-1 text-xs text-slate-700">
          API Key
          <input
            type="text"
            placeholder={`Enter ${provider} API key`}
            value={cfg.apiKey || ''}
            onChange={(e) => update({ apiKey: e.target.value })}
            className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20"
          />
        </label>
      ) : null}

      <label className="grid gap-1 text-xs text-slate-700">
        System Prompt
        <textarea
          className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs font-mono text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20"
          rows={3}
          value={cfg.prompt?.system || ''}
          onChange={(e) => update({ prompt: { ...(cfg.prompt || {}), system: e.target.value } })}
        />
      </label>

      <label className="grid gap-1 text-xs text-slate-700">
        Response Field
        <input
          className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20"
          value={cfg.responseField || ''}
          onChange={(e) => update({ responseField: e.target.value })}
        />
      </label>

      {/* <label className="grid gap-1 text-xs text-slate-700">
        Phone Number ID
        <input
          className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20"
          value={cfg.phone_number_id || cfg.phoneNumberId || selectedPhoneId || ''}
          onChange={(e) => update({ phone_number_id: e.target.value, phoneNumberId: e.target.value })}
        />
      </label> */}

      {/* <label className="grid gap-1 text-xs text-slate-700">
        Knowledge Base ID
        <input
          className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20"
          value={cfg.kbId || ''}
          onChange={(e) => update({ kbId: e.target.value })}
        />
      </label> */}

      {/* <label className="grid gap-1 text-xs text-slate-700">
        Query Template
        <textarea
          className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20"
          rows={3}
          value={cfg.queryTemplate || ''}
          onChange={(e) => update({ queryTemplate: e.target.value })}
        />
      </label> */}

      {/* <div className="grid gap-2 grid-cols-1 sm:grid-cols-2">
        <label className="grid gap-1 text-xs text-slate-700">
          Top K
          <input
            className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20"
            type="number"
            min="1"
            max="20"
            value={cfg.topK ?? 5}
            onChange={(e) => update({ topK: Number(e.target.value) })}
          />
        </label>
        <label className="grid gap-1 text-xs text-slate-700">
          Min Score
          <input
            className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20"
            type="number"
            step="0.01"
            min="0"
            max="1"
            value={cfg.minScore ?? 0.65}
            onChange={(e) => update({ minScore: Number(e.target.value) })}
          />
        </label>
      </div> */}

      {/* <div className="grid gap-2 grid-cols-1 sm:grid-cols-2">
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <input
            className="nodrag w-auto rounded border-slate-300"
            type="checkbox"
            checked={Boolean(cfg.includeCitations)}
            onChange={(e) => update({ includeCitations: e.target.checked })}
          />
          Include Citations
        </label>

        <label className="grid gap-1 text-xs text-slate-700">
          Output Variable
          <input
            className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20"
            value={cfg.outputVariable || ''}
            onChange={(e) => update({ outputVariable: e.target.value })}
          />
        </label>
      </div> */}

      {/* <label className="grid gap-1 text-xs text-slate-700">
        Fallback
        <select
          className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20"
          value={cfg.fallbackBehavior || 'continue_without_context'}
          onChange={(e) => update({ fallbackBehavior: e.target.value })}
        >
          <option value="continue_without_context">Continue without context</option>
          <option value="stop_workflow">Stop workflow</option>
          <option value="route_to_human">Route to human</option>
        </select>
      </label> */}

      <div className="space-y-2 border border-slate-200 rounded-lg p-2">
        <label className="text-xs font-medium text-gray-600">Upload Knowledge PDF</label>

        <input
          type="file"
          accept="application/pdf"
          onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
          className="w-full text-sm"
        />

        <p className="text-xs text-gray-500">Files are shared across workflows for this WhatsApp number.</p>

        {userFiles.map((file) => (
          <div key={file.file_id} className="flex justify-between items-center border rounded-md px-3 py-2 text-sm">
            <span>{file.file_name}</span>
            <button
              onClick={() => handleDeleteFile(file)}
              className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        ))}

        <button
          onClick={handleUpload}
          disabled={!isUploadValid() || uploadStatus === 'loading'}
          className="px-3 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center space-x-2"
        >
          {uploadStatus === 'loading' ? (
            <span className="loader border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
          ) : null}
          <span>Save File</span>
        </button>

        {uploadStatus === 'success' ? (
          <p className="text-green-600 text-xs flex items-center space-x-1">
            <span>Status:</span>
            <span>{uploadMessage}</span>
          </p>
        ) : null}

        {uploadStatus === 'error' ? (
          <p className="text-red-600 text-xs flex items-center space-x-1">
            <span>Status:</span>
            <span>{uploadMessage}</span>
          </p>
        ) : null}
      </div>
    </div>
  );
}


